import { RotateLeftIcon, XMarkIcon } from '@/components/Icons'
import { useUiStrings } from '@/hooks/useUiStrings'
import { useStore } from '@/store/useStore'
import { formatDateTime } from '@/utils/formatDateTime'
import {
  canRestoreProjectContextVersion,
  type ProjectContextDiffSegment,
  type ProjectContextVersion,
} from '../projectContextUi'

type Props = {
  /** The version on screen; null keeps the modal closed. */
  version: ProjectContextVersion | null
  onClose: () => void
  /** Puts this version back in use. Absent while there is nothing to call. */
  onRestore?: (version: ProjectContextVersion) => void
  busy?: boolean
}

/** One AI version's text, with its additions and removals marked apart. */
function DiffText({ segments }: { segments: ProjectContextDiffSegment[] }) {
  return (
    <p className="whitespace-pre-wrap">
      {segments.map((segment, index) => {
        if (segment.kind === 'insert') {
          return (
            <mark
              key={index}
              className="rounded-sm bg-yellow-200 px-1 py-0.5 font-medium text-[var(--text-primary)] dark:bg-yellow-400/25"
            >
              {segment.text}
            </mark>
          )
        }
        if (segment.kind === 'delete') {
          return (
            <span key={index} className="text-red-600 line-through dark:text-red-400">
              {segment.text}
            </span>
          )
        }
        return <span key={index}>{segment.text}</span>
      })}
    </p>
  )
}

/**
 * One version's detail: what it changed, what it replaced, and the way back to
 * it. Opens from the versions table, and the footer moves between versions
 * without going back to the list.
 */
export default function ProjectContextVersionModal({
  version,
  onClose,
  onRestore,
  busy = false,
}: Props) {
  const { t } = useUiStrings()
  const uiLocale = useStore((s) => s.uiLocale)

  if (!version) return null

  const segments = version.segments ?? []
  const inserts = segments.filter((segment) => segment.kind === 'insert').length
  const deletes = segments.filter((segment) => segment.kind === 'delete').length
  // A replacement is one change, not two, which is how the design counts them.
  const changeCount = Math.max(inserts, deletes)
  const canRestore = Boolean(onRestore) && canRestoreProjectContextVersion(version)

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="version-detail-title"
        data-testid="version-detail-modal"
        className="flex max-h-[calc(100dvh-2rem)] w-full max-w-[660px] flex-col overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-2xl"
      >
        <div className="shrink-0 px-6 pt-5 pb-3">
          <div className="flex items-start justify-between gap-3">
            <h2
              id="version-detail-title"
              className="text-lg font-bold text-[var(--text-primary)]"
            >
              {t.projectDetailVersionModalTitle} {version.label}
            </h2>
            <button
              type="button"
              aria-label={t.projectDetailVersionCloseDialog}
              onClick={onClose}
              className="-mr-1 rounded-md p-1 text-[var(--text-tertiary)] transition-colors hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-1 flex items-center gap-1.5 text-xs text-[var(--text-secondary)]">
            <span>
              {t.projectDetailVersionCreatedFrom}{' '}
              {version.sourceIsMeeting ? (
                <span className="font-semibold text-blue-600 dark:text-blue-400">
                  {version.sourceLabel}
                </span>
              ) : (
                <span className="font-semibold text-[var(--text-primary)]">
                  {version.sourceLabel}
                </span>
              )}
            </span>
            <span>·</span>
            <span>{formatDateTime(version.createdAt, uiLocale)}</span>
          </div>

          {changeCount > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <span className="font-semibold text-[var(--text-secondary)]">
                {changeCount} {t.projectDetailVersionChanges}
                {version.comparesTo ? ` ${t.projectDetailVersionVs} ${version.comparesTo}` : ''}
              </span>
              <span className="rounded bg-amber-100/90 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-500/15 dark:text-amber-400">
                {t.projectDetailVersionAddedLegend}
              </span>
              <span className="rounded bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-700 dark:bg-rose-500/15 dark:text-rose-400">
                {t.projectDetailVersionRemovedLegend}
              </span>
            </div>
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col px-6 py-2">
          <div className="mb-2 flex shrink-0 items-center justify-between gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">
              {t.projectDetailVersionAiProfileLabel}
            </span>
          </div>
          <div
            data-testid="version-detail-diff-scroll"
            className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-amber-300 bg-amber-50/20 p-4 text-[13px] leading-relaxed text-[var(--text-primary)] dark:border-yellow-500/30 dark:bg-yellow-500/5"
          >
            {segments.length > 0 ? (
              <DiffText segments={segments} />
            ) : (
              <p className="text-[var(--text-tertiary)]">{version.changes}</p>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[var(--border-color)] px-6 py-4">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="rounded-lg border border-[var(--border-color)] px-3.5 py-1.5 text-xs font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-50"
          >
            {t.cancel}
          </button>
          {canRestore && onRestore ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => onRestore(version)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--accent)] px-3.5 py-1.5 text-xs font-semibold text-white shadow-[var(--shadow-sm)] transition-colors hover:bg-[var(--accent-hover)] disabled:opacity-50"
            >
              <RotateLeftIcon className="h-3.5 w-3.5" />
              <span>{t.projectDetailVersionRestore}</span>
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
